import { Box } from '@factorialco/deprecated-design-system'
import { Icon } from '@factorialco/factorial-one-react'
import {
  OneCard,
  RawTag,
  Spinner,
  TagList,
} from '@factorialco/factorial-one-react/dist/experimental'
import { Calendar, People, Pin, StarFilled } from '@factorialco/factorial-one-react/icons/app'
import {
  AtsDeleteJobPostingFavouriteDocument,
  AtsFavouriteJobPostingItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { statusColor, statusLabel } from 'modules/ats/components/JobStatusTag'

import { starButtonStyle } from './index.vanilla.css'

interface FavouriteJobCardProps {
  favouriteJob: AtsFavouriteJobPostingItemFragment
  deletingFavouriteJob: number | null
  onDeleteFavourite: (jobId: number | null) => void
}

export const FavouriteJobCard = ({
  favouriteJob,
  deletingFavouriteJob,
  onDeleteFavourite,
}: FavouriteJobCardProps) => {
  const { navigation } = useNavigation()
  const {
    access: { id: accessId },
  } = useCurrentSession()

  const [deleteAtsFavouriteJobPosting, { loading }] = useMutation(
    AtsDeleteJobPostingFavouriteDocument,
    {
      extractorFn: (data) => data.atsMutations.deleteAtsFavouriteJobPosting,
    }
  )

  const onDelete = async () => {
    onDeleteFavourite(favouriteJob.id)
    const result = await deleteAtsFavouriteJobPosting({
      variables: { id: favouriteJob.id, authorId: accessId },
      update(cache) {
        cache.evict({ id: cache.identify(favouriteJob) })
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    onDeleteFavourite(null)

    return handler
  }

  const {
    title,
    status,
    workplaceTypeLabel,
    location,
    id: jobId,
    publishedAt,
    applicationphasesConnection,
  } = favouriteJob.atsJobPosting

  const phases = applicationphasesConnection.nodes.filter(
    (phase) => phase?.activeApplicationsCount && phase?.activeApplicationsCount > 0
  )

  return (
    <Box position="relative">
      <OneCard
        title={title}
        link={navigation.ats.jobs.jobsId.applications.fullPath({
          jobId,
        })}
        metadata={[
          // @ts-expect-error StatusMetadata requires icon but it works without it
          {
            type: 'status',
            label: statusLabel(status),
            status: statusColor(status),
          },
          {
            type: 'text',
            title: `${location?.name} ${workplaceTypeLabel ? `(${workplaceTypeLabel})` : ''}`,
            icon: Pin,
          },
          {
            type: 'text',
            title: publishedAt ? dateBackendDateTimeToRelativeTime(publishedAt) : '-',
            icon: Calendar,
          },
        ]}
      >
        <Box gap="s6" flexDirection="row" alignItems="center">
          <Icon icon={People} className="text-f1-icon" size="md" />
          {phases.length > 0 ? (
            <TagList
              type="dot"
              layout="fill"
              tags={phases.map((phase) => ({
                text: phase.activeApplicationsCount?.toString() ?? '0',
                color: phaseColor(phase?.hiringStage?.name ?? 'default'),
                description: phase?.name,
              }))}
            />
          ) : (
            <RawTag text={i18n.t('ats.jobs.favourites.no_candidates')} />
          )}
        </Box>
      </OneCard>

      <Box
        position="absolute"
        background="grey000White"
        className={starButtonStyle}
        alignItems="center"
        justifyContent="center"
        borderRadius={{
          all: 'abs006',
        }}
        style={{
          top: '12px',
          right: '12px',
          cursor: 'pointer',
        }}
        onClick={onDelete}
      >
        <Icon icon={StarFilled} className="text-f1-border-selected-bold" size="lg" />
      </Box>

      {(deletingFavouriteJob === favouriteJob.id || loading) && (
        <Box
          position="absolute"
          background="grey000White"
          style={{
            inset: '0px',
            opacity: '0.5',
          }}
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="large" />
        </Box>
      )}
    </Box>
  )
}
